import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../localization/useAppTranslation';
import { Dialog } from '../dialog/Dialog';
import {
  SAVED_PROMPT_TEXT_MAX_LENGTH,
  SAVED_PROMPT_TITLE_MAX_LENGTH,
  type SavedPrompt,
} from '../../types/preferences';
import '../../styles/SavedPromptDialog.css';

type SavedPromptEditorMode = 'create' | 'edit';

interface SavedPromptEditorDialogProps {
  isOpen: boolean;
  mode: SavedPromptEditorMode;
  prompt: SavedPrompt | null;
  initialTitle?: string;
  initialPromptText?: string;
  onSave: (title: string, promptText: string) => Promise<void>;
  onClose: () => void;
}

export const SavedPromptEditorDialog: React.FC<SavedPromptEditorDialogProps> = ({
  isOpen,
  mode,
  prompt,
  initialTitle: createInitialTitle,
  initialPromptText: createInitialPromptText,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation('preferences');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const initialTitle = mode === 'create'
    ? createInitialTitle || ''
    : prompt?.title || '';
  const initialPromptText = mode === 'create'
    ? createInitialPromptText || ''
    : prompt?.prompt_text || '';
  const [title, setTitle] = useState(initialTitle);
  const [promptText, setPromptText] = useState(initialPromptText);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setTitle(initialTitle);
    setPromptText(initialPromptText);
    setValidationError(null);
    setSaveError(null);
    setIsSaving(false);
    setIsDiscardConfirmationOpen(false);
  }, [initialPromptText, initialTitle, isOpen]);

  const isDirty = title !== initialTitle || promptText !== initialPromptText;
  const formId = mode === 'create' ? 'new-saved-prompt-form' : 'edit-saved-prompt-form';
  const dialogTitle = mode === 'create'
    ? t('savedPrompts.editor.newTitle')
    : t('savedPrompts.editor.editTitle');
  const isFormValid = (
    title.trim().length > 0
    && title.trim().length <= SAVED_PROMPT_TITLE_MAX_LENGTH
    && promptText.trim().length > 0
    && promptText.length <= SAVED_PROMPT_TEXT_MAX_LENGTH
  );

  const requestClose = () => {
    if (isSaving) {
      return;
    }

    if (isDirty) {
      setIsDiscardConfirmationOpen(true);
      return;
    }

    onClose();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) {
      return;
    }

    const normalizedTitle = title.trim();
    if (!normalizedTitle) {
      setValidationError(t('savedPrompts.validation.titleRequired'));
      return;
    }
    if (normalizedTitle.length > SAVED_PROMPT_TITLE_MAX_LENGTH) {
      setValidationError(t('savedPrompts.validation.titleMax', { maximum: SAVED_PROMPT_TITLE_MAX_LENGTH }));
      return;
    }
    if (!promptText.trim()) {
      setValidationError(t('savedPrompts.validation.promptTextRequired'));
      return;
    }
    if (promptText.length > SAVED_PROMPT_TEXT_MAX_LENGTH) {
      setValidationError(t('savedPrompts.validation.promptTextMax', { maximum: SAVED_PROMPT_TEXT_MAX_LENGTH }));
      return;
    }

    setValidationError(null);
    setSaveError(null);
    setIsSaving(true);
    try {
      await onSave(normalizedTitle, promptText);
      onClose();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : t('savedPrompts.errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isDiscardConfirmationOpen) {
    return (
      <Dialog
        isOpen={isOpen}
        title={t('savedPrompts.editor.discardChangesTitle')}
        onRequestClose={() => setIsDiscardConfirmationOpen(false)}
        footer={(
          <>
            <button
              type="button"
              className="dialog-button dialog-button-secondary"
              onClick={() => setIsDiscardConfirmationOpen(false)}
            >
              {t('savedPrompts.editor.keepEditing')}
            </button>
            <button
              type="button"
              className="dialog-button dialog-button-danger"
              onClick={onClose}
            >
              {t('savedPrompts.editor.discardChanges')}
            </button>
          </>
        )}
      >
        <p>{t('savedPrompts.editor.discardChangesMessage')}</p>
      </Dialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      title={dialogTitle}
      onRequestClose={requestClose}
      initialFocusRef={titleInputRef}
      className="saved-prompt-editor-dialog"
      footer={(
        <>
          <button
            type="button"
            className="dialog-button dialog-button-secondary"
            onClick={requestClose}
            disabled={isSaving}
          >
            {t('savedPrompts.editor.cancel')}
          </button>
          <button
            type="submit"
            form={formId}
            className="dialog-button dialog-button-primary"
            disabled={isSaving || !isFormValid}
          >
            {isSaving ? t('savedPrompts.editor.saving') : t('savedPrompts.editor.save')}
          </button>
        </>
      )}
    >
      <form id={formId} className="saved-prompt-editor-form" onSubmit={handleSubmit} noValidate>
        <div className="saved-prompt-editor-field">
          <label htmlFor="saved-prompt-title">{t('savedPrompts.form.titleLabel')}</label>
          <input
            ref={titleInputRef}
            id="saved-prompt-title"
            type="text"
            value={title}
            maxLength={SAVED_PROMPT_TITLE_MAX_LENGTH}
            onChange={(event) => setTitle(event.target.value)}
            disabled={isSaving}
            aria-describedby="saved-prompt-title-count"
          />
          <span id="saved-prompt-title-count" className="saved-prompt-character-count">
            {t('savedPrompts.form.characterCount', {
              count: title.length,
              maximum: SAVED_PROMPT_TITLE_MAX_LENGTH,
            })}
          </span>
        </div>
        <div className="saved-prompt-editor-field">
          <label htmlFor="saved-prompt-text">{t('savedPrompts.form.promptTextLabel')}</label>
          <textarea
            id="saved-prompt-text"
            value={promptText}
            maxLength={SAVED_PROMPT_TEXT_MAX_LENGTH}
            onChange={(event) => setPromptText(event.target.value)}
            disabled={isSaving}
            aria-describedby="saved-prompt-text-count"
          />
          <span id="saved-prompt-text-count" className="saved-prompt-character-count">
            {t('savedPrompts.form.characterCount', {
              count: promptText.length,
              maximum: SAVED_PROMPT_TEXT_MAX_LENGTH,
            })}
          </span>
        </div>
        {validationError && <div className="saved-prompt-editor-error" role="alert">{validationError}</div>}
        {saveError && <div className="saved-prompt-editor-error" role="alert">{saveError}</div>}
      </form>
    </Dialog>
  );
};